/**
 * @author bhouston / http://exocortex.com
 * @author WestLangley / http://github.com/WestLangley
 */
/* Pruned version of THREE.Box3, for use in the LMV web worker */

import { LmvVector3 } from './LmvVector3';

/**
 * Represents a 3D axis-aligned bounding box.
 *
 * @constructor
 * @class
 * @memberof Autodesk.Viewing.Math
 *
 * @param {Autodesk.Viewing.Math.Vector3} [min] - The minimum (lower) corner. Defaults to (+Infinity, +Infinity, +Infinity).
 * @param {Autodesk.Viewing.Math.Vector3} [max] - The maximum (upper) corner. Defaults to (-Infinity, -Infinity, -Infinity).
 *
 * @alias Autodesk.Viewing.Math.Box3
 */
export function LmvBox3(min, max) {

  /**
   * The minimum (lower) corner of the box.
   * @type {Autodesk.Viewing.Math.Vector3}
   */
  this.min = min !== undefined ? min : new LmvVector3(Infinity, Infinity, Infinity);
  /**
   * The maximum (upper) corner of the box.
   * @type {Autodesk.Viewing.Math.Vector3}
   */
  this.max = max !== undefined ? max : new LmvVector3(-Infinity, -Infinity, -Infinity);

}


LmvBox3.prototype.constructor = LmvBox3;

// Declare the type in the namespace, so that we get the correct return types for the functions below,
// while maintaining the documentation.
/** @typedef {LmvMatrix4} Autodesk.Viewing.Math.Matrix4 */
/** @typedef {LmvVector3} Autodesk.Viewing.Math.Vector3 */
/** @typedef {LmvBox3} Autodesk.Viewing.Math.Box3 */

/**
 * Set the min and max corners of this box.
 * @param {Autodesk.Viewing.Math.Vector3} min - The minimum corner.
 * @param {Autodesk.Viewing.Math.Vector3} max - The maximum corner.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#set
 */
LmvBox3.prototype.set = function (min, max) {

  this.min.copy(min);
  this.max.copy(max);

  return this;

};

/**
 * Set this box to enclose the given points.
 * @param {Autodesk.Viewing.Math.Vector3[]} points - The points to enclose.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#setFromPoints
 */
LmvBox3.prototype.setFromPoints = function (points) {

  this.makeEmpty();

  for (var i = 0, il = points.length; i < il; i++) {

    this.expandByPoint(points[i]);

  }

  return this;

};

/**
 * Set this box to the world-axis-aligned bounding box of an object and its descendants,
 * accounting for each node's world transform. Supports both legacy `geometry.vertices`
 * and BufferGeometry `geometry.attributes.position` shapes via duck-typing.
 * @function
 * @param {Object} object - An object with `updateMatrixWorld()`, `traverse(callback)`,
 *     `matrixWorld`, and optional `geometry` properties. Compatible with THREE.Object3D
 *     and any duck-typed equivalent.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#setFromObject
 * @private
 */
LmvBox3.prototype.setFromObject = function () {

  var v1 = new LmvVector3();

  return function (object) {

    var scope = this;

    object.updateMatrixWorld(true);

    this.makeEmpty();

    object.traverse(function (node) {

      var geometry = node.geometry;
      if (!geometry) return;

      if (geometry.vertices) {
        // Legacy Geometry shape
        var vertices = geometry.vertices;
        for (let i = 0, il = vertices.length; i < il; i++) {
          v1.copy(vertices[i]);
          v1.applyMatrix4(node.matrixWorld);
          scope.expandByPoint(v1);
        }
      } else if (geometry.attributes && geometry.attributes['position']) {
        // BufferGeometry shape
        var positions = geometry.attributes['position'].array;
        for (let i = 0, il = positions.length; i < il; i += 3) {
          v1.set(positions[i], positions[i + 1], positions[i + 2]);
          v1.applyMatrix4(node.matrixWorld);
          scope.expandByPoint(v1);
        }
      }

    });

    return this;

  };

}();

/**
 * Set this box from six consecutive numbers in an array: min x, y, z then max x, y, z.
 * @param {number[]} array - The source array.
 * @param {number} offset - The offset into the array.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#setFromArray
 */
LmvBox3.prototype.setFromArray = function (array, offset) {

  this.min.x = array[offset];
  this.min.y = array[offset + 1];
  this.min.z = array[offset + 2];

  this.max.x = array[offset + 3];
  this.max.y = array[offset + 4];
  this.max.z = array[offset + 5];

  return this;

};

/**
 * Write this box into an array as six consecutive numbers: min x, y, z then max x, y, z.
 * @param {number[]} array - The target array.
 * @param {number} offset - The offset into the array.
 *
 * @alias Autodesk.Viewing.Math.Box3#copyToArray
 */
LmvBox3.prototype.copyToArray = function (array, offset) {

  array[offset] = this.min.x;
  array[offset + 1] = this.min.y;
  array[offset + 2] = this.min.z;

  array[offset + 3] = this.max.x;
  array[offset + 4] = this.max.y;
  array[offset + 5] = this.max.z;

};

/**
 * Set this box from a center point and a size vector.
 * @function
 * @param {Autodesk.Viewing.Math.Vector3} center - The center point.
 * @param {Autodesk.Viewing.Math.Vector3} size - The size of the box along each axis.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#setFromCenterAndSize
 */
LmvBox3.prototype.setFromCenterAndSize = function () {

  var v1 = new LmvVector3();

  return function (center, size) {

    var halfSize = v1.copy(size).multiplyScalar(0.5);

    this.min.copy(center).sub(halfSize);
    this.max.copy(center).add(halfSize);

    return this;

  };

}();

/**
 * Create a clone of this box.
 * @return {Autodesk.Viewing.Math.Box3} A new box with the same min and max.
 *
 * @alias Autodesk.Viewing.Math.Box3#clone
 */
LmvBox3.prototype.clone = function () {

  return new this.constructor().copy(this);

};

/**
 * Copy the components from another box.
 * @param {Autodesk.Viewing.Math.Box3} box - The box to copy.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#copy
 */
LmvBox3.prototype.copy = function (box) {

  this.min.copy(box.min);
  this.max.copy(box.max);

  return this;

};

/**
 * Set this box to the empty box. Min components become +Infinity, max components become -Infinity.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#makeEmpty
 */
LmvBox3.prototype.makeEmpty = function () {

  this.min.x = this.min.y = this.min.z = Infinity;
  this.max.x = this.max.y = this.max.z = -Infinity;

  return this;

};

/**
 * Check whether this box is empty (any max component is less than its min component).
 * @return {boolean} True if this box has no volume.
 *
 * @alias Autodesk.Viewing.Math.Box3#isEmpty
 */
LmvBox3.prototype.isEmpty = function () {

  // this is a more robust check for empty than ( volume <= 0 ) because volume can get positive with two negative axes

  return this.max.x < this.min.x || this.max.y < this.min.y || this.max.z < this.min.z;

};

/**
 * @deprecated Use isEmpty() instead.
 * @return {boolean} True if this box has no volume.
 *
 * @alias Autodesk.Viewing.Math.Box3#empty
 * @private
 * @note Removed from documentation as it is deprecated; isEmpty() is the documented form.
 */
LmvBox3.prototype.empty = function () {
  return this.isEmpty();
};

/**
 * @deprecated Use getCenter() instead.
 * @param {Autodesk.Viewing.Math.Vector3} [optionalTarget] - Optional output vector.
 * @return {Autodesk.Viewing.Math.Vector3} The center of the box.
 *
 * @alias Autodesk.Viewing.Math.Box3#center
 * @private
 * @note Removed from documentation as it is deprecated; getCenter() is the documented form.
 */
LmvBox3.prototype.center = function (optionalTarget) {
  console.warn("LmvBox3.center() is deprecated. Use LmvBox3.getCenter() instead.");
  return this.getCenter(optionalTarget);
};

/**
 * Get the center point of this box. Returns (0, 0, 0) for an empty box.
 * @param {Autodesk.Viewing.Math.Vector3} [optionalTarget] - Optional output vector. If omitted, a new vector is created.
 * @return {Autodesk.Viewing.Math.Vector3} The center of the box.
 *
 * @alias Autodesk.Viewing.Math.Box3#getCenter
 */
LmvBox3.prototype.getCenter = function (optionalTarget) {

  var result = optionalTarget || new LmvVector3();
  // Empty-box guard: matches THREE.Box3.getCenter behavior from three.js (full).
  // Without this, Infinity + -Infinity yields NaN components.
  if (this.isEmpty()) {
    return result.set(0, 0, 0);
  }
  return result.addVectors(this.min, this.max).multiplyScalar(0.5);

};

/**
 * @deprecated Use getSize() instead.
 * @param {Autodesk.Viewing.Math.Vector3} [optionalTarget] - Optional output vector.
 * @return {Autodesk.Viewing.Math.Vector3} The size of the box along each axis.
 *
 * @alias Autodesk.Viewing.Math.Box3#size
 * @private
 * @note Removed from documentation as it is deprecated; getSize() is the documented form.
 */
LmvBox3.prototype.size = function (optionalTarget) {

  var result = optionalTarget || new LmvVector3();
  return result.subVectors(this.max, this.min);

};

/**
 * Get the size of this box along each axis (max - min).
 * @param {Autodesk.Viewing.Math.Vector3} [optionalTarget] - Optional output vector. If omitted, a new vector is created.
 * @return {Autodesk.Viewing.Math.Vector3} The size of the box along each axis.
 *
 * @alias Autodesk.Viewing.Math.Box3#getSize
 */
LmvBox3.prototype.getSize = function (optionalTarget) {

  var result = optionalTarget || new LmvVector3();
  return result.subVectors(this.max, this.min);

};

/**
 * Expand this box to include the given point.
 * @param {Autodesk.Viewing.Math.Vector3} point - The point to include.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#expandByPoint
 */
LmvBox3.prototype.expandByPoint = function (point) {

  this.min.min(point);
  this.max.max(point);

  return this;

};

/**
 * Expand this box by the given vector. Min is moved by -vector and max by +vector.
 * @param {Autodesk.Viewing.Math.Vector3} vector - The vector to expand by.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#expandByVector
 */
LmvBox3.prototype.expandByVector = function (vector) {

  this.min.sub(vector);
  this.max.add(vector);

  return this;

};

/**
 * Expand this box uniformly by the given scalar. Min is moved by -scalar and max by +scalar on every axis.
 * @param {number} scalar - The scalar amount to expand by.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#expandByScalar
 */
LmvBox3.prototype.expandByScalar = function (scalar) {

  this.min.addScalar(-scalar);
  this.max.addScalar(scalar);

  return this;

};

/**
 * Check whether this box contains the given point.
 * @param {Autodesk.Viewing.Math.Vector3} point - The point to test.
 * @return {boolean} True if the point lies inside or on the boundary of this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#containsPoint
 */
LmvBox3.prototype.containsPoint = function (point) {

  if (point.x < this.min.x || point.x > this.max.x ||
  point.y < this.min.y || point.y > this.max.y ||
  point.z < this.min.z || point.z > this.max.z) {

    return false;

  }

  return true;

};

/**
 * Check whether this box fully contains another box.
 * @param {Autodesk.Viewing.Math.Box3} box - The box to test.
 * @return {boolean} True if the given box lies entirely within this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#containsBox
 */
LmvBox3.prototype.containsBox = function (box) {

  if (this.min.x <= box.min.x && box.max.x <= this.max.x &&
  this.min.y <= box.min.y && box.max.y <= this.max.y &&
  this.min.z <= box.min.z && box.max.z <= this.max.z) {

    return true;

  }

  return false;

};

/**
 * Get the relative position of a point inside this box, in the range [0, 1] per axis when the point lies inside the box.
 * @param {Autodesk.Viewing.Math.Vector3} point - The point to evaluate.
 * @param {Autodesk.Viewing.Math.Vector3} [optionalTarget] - Optional output vector.
 * @return {Autodesk.Viewing.Math.Vector3} The relative position per axis.
 *
 * @alias Autodesk.Viewing.Math.Box3#getParameter
 */
LmvBox3.prototype.getParameter = function (point, optionalTarget) {

  // This can potentially have a divide by zero if the box
  // has a size dimension of 0.

  var result = optionalTarget || new LmvVector3();

  return result.set(
    (point.x - this.min.x) / (this.max.x - this.min.x),
    (point.y - this.min.y) / (this.max.y - this.min.y),
    (point.z - this.min.z) / (this.max.z - this.min.z)
  );

};

/**
 * @deprecated Use intersectsBox() instead.
 * @param {Autodesk.Viewing.Math.Box3} box - The box to test.
 * @return {boolean} True if the boxes overlap.
 *
 * @alias Autodesk.Viewing.Math.Box3#isIntersectionBox
 * @private
 * @note Removed from documentation as it is deprecated; intersectsBox() is the documented form.
 */
LmvBox3.prototype.isIntersectionBox = function (box) {

  // using 6 splitting planes to rule out intersections.

  if (box.max.x < this.min.x || box.min.x > this.max.x ||
  box.max.y < this.min.y || box.min.y > this.max.y ||
  box.max.z < this.min.z || box.min.z > this.max.z) {

    return false;

  }

  return true;

};

/**
 * Check whether this box intersects another box.
 * @param {Autodesk.Viewing.Math.Box3} box - The box to test.
 * @return {boolean} True if the boxes overlap.
 *
 * @alias Autodesk.Viewing.Math.Box3#intersectsBox
 */
LmvBox3.prototype.intersectsBox = function (box) {
  return this.isIntersectionBox(box);
};

/**
 * Clamp the given point to lie inside this box, component-wise.
 * @param {Autodesk.Viewing.Math.Vector3} point - The point to clamp.
 * @param {Autodesk.Viewing.Math.Vector3} [optionalTarget] - Optional output vector.
 * @return {Autodesk.Viewing.Math.Vector3} The clamped point.
 *
 * @alias Autodesk.Viewing.Math.Box3#clampPoint
 */
LmvBox3.prototype.clampPoint = function (point, optionalTarget) {

  var result = optionalTarget || new LmvVector3();
  return result.copy(point).clamp(this.min, this.max);

};

/**
 * Compute the shortest distance from a point to this box. Returns 0 for points inside the box.
 * @function
 * @param {Autodesk.Viewing.Math.Vector3} point - The point.
 * @return {number} The distance from the point to the box.
 *
 * @alias Autodesk.Viewing.Math.Box3#distanceToPoint
 */
LmvBox3.prototype.distanceToPoint = function () {

  var v1 = new LmvVector3();

  return function (point) {

    var clampedPoint = v1.copy(point).clamp(this.min, this.max);
    return clampedPoint.sub(point).length();

  };

}();

/**
 * Set this box to the intersection of itself and another box.
 * @param {Autodesk.Viewing.Math.Box3} box - The other box.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#intersect
 */
LmvBox3.prototype.intersect = function (box) {

  this.min.max(box.min);
  this.max.min(box.max);

  return this;

};

/**
 * Set this box to the union of itself and another box.
 * @param {Autodesk.Viewing.Math.Box3} box - The other box.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#union
 */
LmvBox3.prototype.union = function (box) {

  this.min.min(box.min);
  this.max.max(box.max);

  return this;

};

/**
 * Transform this box by a 4x4 affine matrix. The resulting box is the axis-aligned bounding box of the transformed corners.
 * @function
 * @param {Autodesk.Viewing.Math.Matrix4} matrix - The matrix to apply.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#applyMatrix4
 */
LmvBox3.prototype.applyMatrix4 = function () {

  const min3 = new LmvVector3();
  const max3 = new LmvVector3();

  return function (matrix) {

    if (this.isEmpty()) {
      return this;
    }

    // use Jim Arvo's AABB transform algorithm
    // https://github.com/erich666/GraphicsGems/blob/7ef8b6990e125fa908dfaeb80bc42f4b4fae65b8/gems/TransBox.c
    // More digestible version here: https://stackoverflow.com/questions/6053522/how-to-recalculate-axis-aligned-bounding-box-after-translate-rotate/58630206#58630206
    const e = matrix.elements;
    for (let i = 0; i < 3; i++) {
      let min = e[12 + i];
      let max = min;
      for (let j = 0; j < 3; j++) {
        const cmin = this.min.getComponent(j);
        const cmax = this.max.getComponent(j);
        const m = e[i + j * 4];

        const a = m * cmin;
        const b = m * cmax;

        if (a < b) {
          min += a;
          max += b;
        } else {
          min += b;
          max += a;
        }
      }
      min3.setComponent(i, min);
      max3.setComponent(i, max);
    }
    this.min.copy(min3);
    this.max.copy(max3);

    return this;

  };

}();

/**
 * Translate this box by the given offset.
 * @param {Autodesk.Viewing.Math.Vector3} offset - The translation offset.
 * @return {Autodesk.Viewing.Math.Box3} A reference to this box.
 *
 * @alias Autodesk.Viewing.Math.Box3#translate
 */
LmvBox3.prototype.translate = function (offset) {

  this.min.add(offset);
  this.max.add(offset);

  return this;

};

/**
 * Check whether this box equals another box (component-wise).
 * @param {Autodesk.Viewing.Math.Box3} box - The box to compare.
 * @return {boolean} True if the boxes are equal.
 *
 * @alias Autodesk.Viewing.Math.Box3#equals
 */
LmvBox3.prototype.equals = function (box) {

  return box.min.equals(this.min) && box.max.equals(this.max);

};